import { formatDateTime } from '@localstack/services';
import { IdentityEventScope, IdentityLogEvent } from '@localstack/types';
import { Close } from '@mui/icons-material';
import { Box, CardContent, CardHeader, Chip, Divider, Drawer, IconButton, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ReactElement } from 'react';

type EventLogDrawerProps = {
  event: Optional<IdentityLogEvent>;
  onClose: () => void;
};

const Label = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: 2,
}));

const Field = ({ label, value }: { label: string; value: string }): ReactElement => (
  <Box>
    <Label>{label}</Label>
    <Typography variant="body2" fontFamily="monospace">
      {value}
    </Typography>
  </Box>
);

export const EventLogDrawer = ({ event, onClose }: EventLogDrawerProps): ReactElement => (
  <Drawer open={!!event} onClose={onClose} anchor="right" PaperProps={{ sx: { width: 480 } }}>
    {!!event && (
      <>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color="textSecondary">
                  Identity Event
                </Typography>
                <Box sx={{ flex: 1 }} />
                <IconButton onClick={onClose} size="small">
                  <Close />
                </IconButton>
              </Box>
              <Stack direction="row" gap={1}>
                <Chip
                  label={event.scope}
                  size="small"
                  color={event.scope === IdentityEventScope.SSO ? 'primary' : 'default'}
                />
                <Chip
                  label={event.success ? 'Success' : 'Failed'}
                  size="small"
                  color={event.success ? 'success' : 'error'}
                />
              </Stack>
              <Typography variant="h6">{event.message}</Typography>
            </Box>
          }
        />
        <Divider />
        <CardContent>
          <Stack gap={2.5}>
            {event.created_at && <Field label="Date" value={formatDateTime(event.created_at)} />}
            <Field label="User Email" value={event.user_email ?? '—'} />
            {event.idp_name && <Field label="IDP Name" value={event.idp_name} />}
            {event.extras && Object.keys(event.extras).length > 0 && (
              <Box>
                <Label>Extras</Label>
                <Typography variant="body2" fontFamily="monospace" sx={{ whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(event.extras, null, 2)}
                </Typography>
              </Box>
            )}
          </Stack>
        </CardContent>
      </>
    )}
  </Drawer>
);
